import React, { useState, useRef } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space, Alert } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_supplier_master.css";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";


interface SupplierDataTypeFromDB {
    id: number; 
    supplier_code: string;
    supplier_name: string;
    address: string | null; 
    status: number; 
    status_text: string; 
    
}

interface SupplierTableDataType {
    key: React.Key;
    supplierCode: string;
    supplierName: string;
    address: string;
    status: string;
}

type SupplierDataIndex = keyof SupplierTableDataType;

const fetcher = (url: string) => fetch(url).then((res) => {
    if (!res.ok) {
        throw new Error('Gagal mengambil data supplier');
    }
    return res.json();
});


const BrowserSupplierMaster: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const apiUrl = backendUrl ? `${backendUrl}/api/supplier` : null;

    const { data: rawData, error, isLoading } = useSWR<SupplierDataTypeFromDB[]>(apiUrl, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: SupplierDataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };

    const handleRowDoubleClick = (record: SupplierTableDataType) => {
        console.log("Double clicked row ID:", record.key);
        navigate(`/dashboard/supplier_master/${record.key}`);
    };

    const getColumnSearchProps = (dataIndex: SupplierDataIndex): TableColumnType<SupplierTableDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Tutup
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: { 
            onOpenChange(open) { 
              if (open) {
                setTimeout(() => searchInput.current?.select(), 100);
              }
            }, 
        }, 
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<SupplierTableDataType> = [
        {
            title: 'Supplier Code',
            dataIndex: 'supplierCode',
            key: 'supplierCode',
            width: '15%',
            ...getColumnSearchProps('supplierCode'),
        },
        {
            title: 'Supplier Name',
            dataIndex: 'supplierName',
            key: 'supplierName',
            width: '30%',
            ...getColumnSearchProps('supplierName'),
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
            width: '40%',
            ...getColumnSearchProps('address'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: '15%',
            sorter: (a, b) => a.status.localeCompare(b.status),
        },
    ];

    const tableData: SupplierTableDataType[] = rawData ? rawData.map(item => ({
        key: item.id, 
        supplierCode: item.supplier_code,
        supplierName: item.supplier_name,
        address: item.address || '-', 
        status: item.status_text 
    })) : [];

    if (error) return <Alert message="Error" description={`Gagal memuat data supplier: ${error.message}`} type="error" showIcon />;

    return (
        <div id="iklk" style={transformStyle}>
            <div className="gjs-grid-row" id="ism3o">
                <div className="gjs-grid-column" id="i9ddk">
                    <Breadcrumb
                        items={[
                            {
                                href: "",
                                title: <HomeOutlined />,
                            },
                            {
                                href: "#/dashboard/browser_supplier_master",
                                title: (
                                    <>
                                        <UserOutlined />
                                        <span>Data Supplier</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/supplier_master",
                                title: "Supplier Master",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ib5gi">
                <div className="gjs-grid-column" id="iodhm">
                    <Table<SupplierTableDataType>
                        columns={columns}
                        dataSource={tableData}
                        bordered 
                        size="small" 
                        loading={isLoading}
                        onRow={(record) => {
                            return {
                                onDoubleClick: event => { handleRowDoubleClick(record); }, // Panggil handler saat double click
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserSupplierMaster;